import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { ECharts, EChartsOption } from 'echarts';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import {
    IQueryContext,
    QueryContextDefaultSettings,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { datePartDateTrunc } from 'src/app/utils/sql';
import { DatasourceColumnOldDataLoaderFactoryOptions } from '../common/data-loaders/datasource-column-old';
import { SegmentDatasourceColumnDataLoaderFactoryOptions } from '../common/data-loaders/segments';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { IMultiSelectSettingDashboardItem } from '../common/items/setting/multi-select/multi-select.component';
import { ISingleSelectSettingDashboardItem } from '../common/items/setting/single-select/single-select.component';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { EngagementSessionsDistributionBySessionsDataLoaderFactory } from './data-loaders/engagement-sessions-distribution-by-sessions';
import { MopaEngagementChartDocs } from './mopa-engagement.chart-documentation';
import { BiVizDashboardEngagement } from './sql-maps/bivz-engagement-sessions-with-dimensions-sql-map';

@Component({
    selector: 'app-mopa-engagement',
    templateUrl: './mopa-engagement.component.html',
    styleUrls: ['./mopa-engagement.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class MopaEngagementComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Engagement';
    override DASHBOARD_ID = 'mopa-engagement';

    values = Object.values;

    private gcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    // date aggregation
    // segemnts
    // activity date, app_version, first_open_date, app_store, country_tier, country, traffic_source, install_version, payer, revenue_badge, campaign_name
    override filterDataLoaders = {
        'mopa.engagement.filter.segment': DataLoaderFactories[
            'common.filter.segment'
        ](
            new SegmentDatasourceColumnDataLoaderFactoryOptions(
                'mopa.engagement.filter_segments',
                this.gcpProjectId,
                this.queryCache
            )
        ).create(),
        'mopa.engagement.filter.activity_app_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.engagement.filter.app_version',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ACTIVITY_APP_VERSION',
            datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('ACTIVITY_APP_VERSION'),
        }).create(),
        'mopa.engagement.filter.activity_app_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.engagement.filter.activity_app_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery(
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.engagement.filter.firebase_app_store': DataLoaderFactories[
            'common.filter.datasource_column'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.engagement.filter.firebase_app_store',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_APP_STORE',
                datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
            })
        ).create(),
        'mopa.engagement.filter.firebase_country_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.engagement.filter.firebase_country_tier',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
            datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
            // filterContext: ['FIREBASE_COUNTRY'],
        }).create(),
        'mopa.engagement.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.engagement.filter.firebase_country',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
            filterContext: ['FIREBASE_COUNTRY_TIER'],
        }).create(),
        'mopa.engagement.filter.firebase_country_us_region':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.engagement.filter.firebase_country_us_region',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_US_REGION',
                datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
            }).create(),
        'mopa.engagement.filter.attribution_traffic_source':
            DataLoaderFactories['common.filter.datasource_column'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.engagement.filter.attribution_traffic_source',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                    datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
                })
            ).create(),
        'mopa.engagement.filter.firebase_install_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.engagement.filter.firebase_install_version',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION',
                datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIREBASE_INSTALL_VERSION'
                ),
            })
        ).create(),
        'mopa.engagement.filter.firebase_install_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.engagement.filter.firebase_install_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.engagement.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.engagement.filter.etl_payer',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_PAYER',
            datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
        }).create(),
        'mopa.engagement.filter.etl_revenue_badge': DataLoaderFactories[
            'common.filter.datasource_column'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.engagement.filter.etl_revenue_badge',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ETL_REVENUE_BADGE',
                datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery('ETL_REVENUE_BADGE'),
            })
        ).create(),
        'mopa.engagement.filter.attribution_network_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.engagement.filter.attribution_network_name',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
                datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
            })
        ).create(),
        'mopa.engagement.filter.attribution_campaign_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.engagement.filter.attribution_campaign_name',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
                filterContext: true,
            })
        ).create(),
    };

    override chartDataLoaders = {
        'mopa.engagement.modify_time_engagement_datasource':
            DataLoaderFactories['common.modify_time']({
                createdInstanceId:
                    'mopa.engagement.modify_time_engagement_datasource',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasetId: 'bi_viz',
                tableId: 'dashboard_engagement',
            }).create(),

        'mopa.engagement.sessions_by_day_no': DataLoaderFactories[
            'common.engagement.sessions_by_day_no'
        ]({
            createdInstanceId: 'mopa.engagement.sessions_by_day_no',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),

        'mopa.engagement.sessions_by_date': DataLoaderFactories[
            'common.engagement.sessions_by_date'
        ]({
            createdInstanceId: 'mopa.engagement.sessions_by_date',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),

        'mopa.engagement.sessions_distribution_by_days_0.5min':
            DataLoaderFactories[
                'common.engagement.sessions_distribution_by_days'
            ]({
                createdInstanceId:
                    'mopa.engagement.sessions_distribution_by_days',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                granularity: '0.5min',
            }).create(),

        'mopa.engagement.sessions_distribution_by_days_5min':
            DataLoaderFactories[
                'common.engagement.sessions_distribution_by_days'
            ]({
                createdInstanceId:
                    'mopa.engagement.sessions_distribution_by_days',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                granularity: '5min',
            }).create(),

        'mopa.engagement.sessions_distribution_by_sessions_0.5min':
            DataLoaderFactories[
                'common.engagement.sessions_distribution_by_sessions'
            ]({
                createdInstanceId:
                    'mopa.engagement.sessions_distribution_by_sessions',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                granularity: '0.5min',
            }).create(),

        'mopa.engagement.sessions_distribution_by_sessions_5min':
            DataLoaderFactories[
                'common.engagement.sessions_distribution_by_sessions'
            ]({
                createdInstanceId:
                    'mopa.engagement.sessions_distribution_by_sessions',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                granularity: '5min',
            }).create(),
        // 'mopa.engagement.engagementdash_activity': DataLoaderFactories[
        //     'common.engagement.engagementdash_activity'
        // ]({
        //     createdInstanceId: 'mopa.engagement.engagementdash_activity',
        //     gcpProjectId: this.gcpProjectId,
        //     queryCacheService: this.queryCache,
        // }).create(),
        // 'mopa.engagement.engagementdash_30days_engagement': DataLoaderFactories[
        //     'common.engagement.engagementdash_30days_engagement'
        // ]({
        //     createdInstanceId: 'mopa.engagement.engagementdash_30days_engagement',
        //     gcpProjectId: this.gcpProjectId,
        //     queryCacheService: this.queryCache,
        // }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////
    settingSelectedDayAndSession: IMultiSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.ENGAGEMENT_SELECTED_DAY_AND_SESSION,
            viewtype: 'multiSelect',
            displayName: 'Selected Day/Session',
            value:
                this.getInitialContext().settings[
                    QueryContextSettingID.ENGAGEMENT_SELECTED_DAY_AND_SESSION
                ] ?? [],
            selectOptions: [
                0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14,
            ].map((d) => ({
                value: d,
                label: `${d}`,
            })),
        },
    };

    settingItemDateAggregation: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.DATE_AGGREGATION,
            viewtype: 'singleSelect',
            displayName: 'Date aggregation',
            value: 'day' as datePartDateTrunc,
            selectOptions: ['day', 'week', 'month', 'quarter', 'year'] as
                | 'day'
                | datePartDateTrunc[],
        },
    };

    // CHARTS
    modify_time_engagement_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.engagement.modify_time_engagement_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'mopa.engagement.modify_time_engagement_datasource',
                'value'
            )
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.engagement.modify_time_engagement_datasource'
            ].emitter,
    };

    med_sess_length_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.engagement.chart.med_sess_length_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Engagement (wip)',
            createdInstanceId:
                'mopa.engagement.chart.med_sess_length_by_day_no',
            dashboardItem: {
                title: 'Median session lenght per user by day number',
                chartDocumentation:
                    MopaEngagementChartDocs.med_sess_length_by_day_no,
            },
            xAxisType: 'value',
            xColumnLabel: 'day_no',
            yColumnLabel: 'median_sess_length_mins',
            metricName: 'Median session length',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.engagement.sessions_by_day_no'].emitter,
    };
    avg_no_sessions_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.engagement.chart.avg_no_sessions_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'engagement',
            createdInstanceId:
                'mopa.engagement.chart.avg_no_sessions_by_day_no',
            dashboardItem: {
                title: 'Number of sessions per user by day number',
                chartDocumentation:
                    MopaEngagementChartDocs.avg_no_sessions_by_day_no,
            },
            xAxisType: 'value',
            xColumnLabel: 'day_no',
            yColumnLabel: 'avg_sess_per_user',
            metricName: 'Avg sessions per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.engagement.sessions_by_day_no'].emitter,
    };
    med_sess_length_by_date: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.engagement.chart.med_sess_length_by_date',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'engagement',
            createdInstanceId: 'mopa.engagement.chart.med_sess_length_by_date',
            dashboardItem: {
                title: 'Median session lenght per user by date',
                chartDocumentation:
                    MopaEngagementChartDocs.med_sess_length_by_date,
            },
            xAxisType: 'time',
            xColumnLabel: 'date',
            yColumnLabel: 'median_sess_length_mins',
            metricName: 'Median session length',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.createAxisLabelFormatter(
                                EChartsAxisHelper.axisLabelDateFormatter,
                                context
                            ),
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.engagement.sessions_by_date'].emitter,
    };
    avg_no_sessions_by_date: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.engagement.chart.avg_no_sessions_by_date',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'engagement',
            createdInstanceId: 'mopa.engagement.chart.avg_no_sessions_by_date',
            dashboardItem: {
                title: 'Number of sessions per user by date',
                chartDocumentation:
                    MopaEngagementChartDocs.avg_no_sessions_by_date,
            },
            xAxisType: 'time',
            xColumnLabel: 'date',
            yColumnLabel: 'avg_sess_per_user',
            metricName: 'Avg sessions per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.createAxisLabelFormatter(
                                EChartsAxisHelper.axisLabelDateFormatter,
                                context
                            ),
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.engagement.sessions_by_date'].emitter,
    };
    sessions_distribution_by_days_05min: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.engagement.chart.sessions_distribution_by_days_05min',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'engagement',
            createdInstanceId:
                'mopa.engagement.chart.sessions_distribution_by_days_05min',
            dashboardItem: {
                title: (context) => {
                    const selectedDaysAndSessions =
                        context.settings['engagement.selectedDayAndSession'] ??
                        QueryContextDefaultSettings[
                            QueryContextSettingID
                                .ENGAGEMENT_SELECTED_DAY_AND_SESSION
                        ]!;
                    return `Session length distribution of selected days: ${selectedDaysAndSessions
                        .map((d) => `d${d}`)
                        .join(', ')}`;
                },
                chartDocumentation:
                    MopaEngagementChartDocs.sessions_distribution_by_days_05min,
            },
            xColumnLabel: 'session_length_bucket',
            yColumnLabel: 'sample',
            metricName: 'Sessions',
            metricsGetter: (context: IQueryContext) => {
                const selectedDaysAndSessions = context.settings[
                    'engagement.selectedDayAndSession'
                ] ?? ['3'];
                const metrics: Record<
                    string,
                    { echartsSeriesOption: {}; name: string }
                > = {};
                selectedDaysAndSessions.forEach((d) => {
                    const metricName = `d${d}`;
                    metrics[`d${d}`] = {
                        echartsSeriesOption: {},
                        name: metricName,
                    };
                });
                return metrics;
            },
            echartsSeriesType: 'bar',
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            // hardcoded histogram bin size 0.5min
                            `Duration: ${label} mins`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.engagement.sessions_distribution_by_days_0.5min'
            ].emitter,
    };
    sessions_distribution_by_sessions_05min: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.engagement.chart.sessions_distribution_by_sessions_05min',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'engagement',
            createdInstanceId:
                'mopa.engagement.chart.sessions_distribution_by_sessions_05min',
            dashboardItem: {
                title: (context) => {
                    const selectedDaysAndSessions =
                        EngagementSessionsDistributionBySessionsDataLoaderFactory.getSelectedSessions(
                            context
                        );
                    return `Session length distribution of selected sessions: ${selectedDaysAndSessions
                        .map((d) => `s${d}`)
                        .join(', ')}`;
                },
                chartDocumentation:
                    MopaEngagementChartDocs.sessions_distribution_by_sessions_05min,
            },
            xColumnLabel: 'session_length_bucket',
            yColumnLabel: 'sample',
            metricName: 'Sessions',
            echartsSeriesType: 'bar',
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            metricsGetter: (context: IQueryContext) => {
                const selectedDaysAndSessions =
                    EngagementSessionsDistributionBySessionsDataLoaderFactory.getSelectedSessions(
                        context
                    );
                const metrics: Record<
                    string,
                    { echartsSeriesOption: {}; name: string }
                > = {};
                selectedDaysAndSessions.forEach((d) => {
                    const metricName = `s${d}`;
                    metrics[`s${d}`] = {
                        echartsSeriesOption: {},
                        name: metricName,
                    };
                });
                return metrics;
            },
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            // hardcoded histogram bin size 0.5min
                            `Duration: ${label} mins`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.engagement.sessions_distribution_by_sessions_0.5min'
            ].emitter,
    };
    sessions_distribution_by_days_5min: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.engagement.chart.sessions_distribution_by_days_5min',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'engagement',
            createdInstanceId:
                'mopa.engagement.chart.sessions_distribution_by_days_5min',
            dashboardItem: {
                title: (context) => {
                    const selectedDaysAndSessions =
                        context.settings['engagement.selectedDayAndSession'] ??
                        QueryContextDefaultSettings[
                            QueryContextSettingID
                                .ENGAGEMENT_SELECTED_DAY_AND_SESSION
                        ]!;
                    return `Session length distribution of selected days: ${selectedDaysAndSessions
                        .map((d) => `d${d}`)
                        .join(', ')}`;
                },
                chartDocumentation:
                    MopaEngagementChartDocs.sessions_distribution_by_days_5min,
            },
            xColumnLabel: 'session_length_bucket',
            yColumnLabel: 'sample',
            metricName: 'Sessions',
            metricsGetter: (context: IQueryContext) => {
                const selectedDaysAndSessions = context.settings[
                    'engagement.selectedDayAndSession'
                ] ?? ['3'];
                const metrics: Record<
                    string,
                    { echartsSeriesOption: {}; name: string }
                > = {};
                selectedDaysAndSessions.forEach((d) => {
                    const metricName = `d${d}`;
                    metrics[`d${d}`] = {
                        echartsSeriesOption: {},
                        name: metricName,
                    };
                });
                return metrics;
            },
            echartsSeriesType: 'bar',
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            // hardcoded histogram bin size 0.5min
                            `Duration: ${label} mins`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.engagement.sessions_distribution_by_days_5min'
            ].emitter,
    };
    sessions_distribution_by_sessions_5min: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.engagement.chart.sessions_distribution_by_sessions_5min',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'engagement',
            createdInstanceId:
                'mopa.engagement.chart.sessions_distribution_by_sessions_5min',
            dashboardItem: {
                title: (context) => {
                    const selectedDaysAndSessions =
                        EngagementSessionsDistributionBySessionsDataLoaderFactory.getSelectedSessions(
                            context
                        );
                    return `Session length distribution of selected sessions: ${selectedDaysAndSessions
                        .map((d) => `s${d}`)
                        .join(', ')}`;
                },
                chartDocumentation:
                    MopaEngagementChartDocs.sessions_distribution_by_sessions_5min,
            },
            xColumnLabel: 'session_length_bucket',
            yColumnLabel: 'sample',
            metricName: 'Sessions',
            echartsSeriesType: 'bar',
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            metricsGetter: (context: IQueryContext) => {
                const selectedDaysAndSessions =
                    EngagementSessionsDistributionBySessionsDataLoaderFactory.getSelectedSessions(
                        context
                    );
                const metrics: Record<
                    string,
                    { echartsSeriesOption: {}; name: string }
                > = {};
                selectedDaysAndSessions.forEach((d) => {
                    const metricName = `s${d}`;
                    metrics[`s${d}`] = {
                        echartsSeriesOption: {},
                        name: metricName,
                    };
                });
                return metrics;
            },
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            // hardcoded histogram bin size 0.5min
                            `Duration: ${label} mins`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.engagement.sessions_distribution_by_sessions_5min'
            ].emitter,
    };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        ACTIVITY_DATE: {
            id: 'mopa.engagement.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'mopa.engagement.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
                splitable: false,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        ACTIVITY_APP_VERSION_GROUP_MINOR: {
            id: 'mopa.engagement.filter.app_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.engagement.filter.app_version_group_minor',
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.engagement.filter.activity_app_version_group_minor'
                ].emitter,
        },
        ACTIVITY_APP_VERSION: {
            id: 'mopa.engagement.filter.app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.engagement.filter.app_version',
                    'ACTIVITY_APP_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.engagement.filter.activity_app_version'
                ].emitter,
        },
        SEGMENT: {
            id: 'mopa.engagement.filter.segments',
            dashboardItemFactory: DashboardItemFactories['filter.segments']({
                createdInstanceId: 'mopa.engagement.filter.segments',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.engagement.filter.segment']
                    .emitter,
        },
        FIREBASE_FIRST_OPEN_DATE: {
            id: 'mopa.engagement.filter.firebase_first_open_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId:
                    'mopa.engagement.filter.firebase_first_open_date',
                datasourceColumnId: 'FIREBASE_FIRST_OPEN_DATE',
                splitable: true,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIREBASE_APP_STORE: {
            id: 'mopa.engagement.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.engagement.filter.firebase_app_store',
                    'FIREBASE_APP_STORE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.engagement.filter.firebase_app_store'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'mopa.engagement.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.engagement.filter.firebase_country_tier',
                    'FIREBASE_COUNTRY_TIER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.engagement.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'mopa.engagement.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.engagement.filter.firebase_country',
                    'FIREBASE_COUNTRY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.engagement.filter.firebase_country'
                ].emitter,
        },
        FIREBASE_COUNTRY_US_REGION: {
            id: 'mopa.engagement.filter.firebase_country_us_region',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.engagement.filter.firebase_country_us_region',
                    'FIREBASE_COUNTRY_US_REGION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.engagement.filter.firebase_country_us_region'
                ].emitter,
        },
        ATTRIBUTION_TRAFFIC_SOURCE: {
            id: 'mopa.engagement.filter.attribution_traffic_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.engagement.filter.attribution_traffic_source',
                    'ATTRIBUTION_TRAFFIC_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.engagement.filter.attribution_traffic_source'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
            id: 'mopa.engagement.filter.firebase_install_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.engagement.filter.firebase_install_version_group_minor',
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.engagement.filter.firebase_install_version_group_minor'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION: {
            id: 'mopa.engagement.filter.firebase_install_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.engagement.filter.firebase_install_version',
                    'FIREBASE_INSTALL_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.engagement.filter.firebase_install_version'
                ].emitter,
        },
        ETL_PAYER: {
            id: 'mopa.engagement.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.engagement.filter.etl_payer',
                    'ETL_PAYER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.engagement.filter.etl_payer']
                    .emitter,
        },
        ETL_REVENUE_BADGE: {
            id: 'mopa.engagement.filter.etl_revenue_badge',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.engagement.filter.etl_revenue_badge',
                    'ETL_REVENUE_BADGE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.engagement.filter.etl_revenue_badge'
                ].emitter,
        },
        ATTRIBUTION_NETWORK_NAME: {
            id: 'mopa.engagement.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.engagement.filter.attribution_network_name',
                    'ATTRIBUTION_NETWORK_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.engagement.filter.attribution_network_name'
                ].emitter,
        },
        ATTRIBUTION_CAMPAIGN_NAME: {
            id: 'mopa.engagement.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.engagement.filter.attribution_campaign_name',
                    'ATTRIBUTION_CAMPAIGN_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.engagement.filter.attribution_campaign_name'
                ].emitter,
        },
    };

    overridengOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'MopaEngagementComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }
    ngAfterViewInit() {
        console.log({
            msg: 'MopaEngagementComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const firebaseAppStoreIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_APP_STORE',
                state: {
                    value: {
                        options: [
                            'Amazon Appstore',
                            'Google Play',
                            'Microsoft Store',
                            'iTunes',
                            'iTunes macOS',
                        ],
                        filterType: 'include',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const firebaseCountryTierIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_COUNTRY_TIER',
                state: {
                    value: {
                        options: ['Tier 1', 'Tier 2', 'Tier 3'],
                        filterType: 'include',
                    },
                    // filterType: 'include',
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: {
                        preset: 'last 365 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            // firebaseAppStoreIC,
            // firebaseCountryTierIC,
            activityDateIC,
        ];

        initialContext.settings = {
            [QueryContextSettingID.ENGAGEMENT_SELECTED_DAY_AND_SESSION]:
                QueryContextDefaultSettings[
                    QueryContextSettingID.ENGAGEMENT_SELECTED_DAY_AND_SESSION
                ]!,
        };
        return initialContext;
    }
}
